import { useState, useEffect } from 'react';
import {
  useTranslation,
  RadioButton,
  Input,
  DropDown,
  Theme,
  Checkbox,
  InputValidations,
  AccountType,
  IconStyle,
} from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import type {
  DynamicObjectInterface,
  SponsorSearchProps,
  SponsorSearchResponseInner,
  CountryInterface,
} from '@commerce-profile-webcomponents/sdk';
import { profileApi, InternationalSponsorTypeEnum, updateRegistrationData } from '@commerce-profile-webcomponents/sdk';
import { SponsorCard } from '../SponsorCard/SponsorCard';
import { useCountryMasterData } from '../../../utils/masterdata';
import styles from './InternationalSponsor.module.scss';
import type { InternationalSponsorProps } from './types/propTypes';

export const InternationalSponsor = ({ updateState }: InternationalSponsorProps) => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { selectSponsor } = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const { selectSponsor: sponsorCms } = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);

  const {
    sponsor,
    isInternational,
    internationalSponsorType,
    internationalSponsor,
    internationalSponsorCountryCode,
    internationalSponsorTncCheckbox,
    internationalSposnorVerified,
  } = selectSponsor;

  // Current country for display
  const { currentCountry, data: country } = useCountryMasterData();
  // Search operation states
  const [countrySearchTxt, setCountrySearchTxt] = useState<string>('');
  const [sponsorNbr, setSponsorNbr] = useState<string>('');
  const [verificationStatus, setVerificationStatus] = useState<InputValidations>(InputValidations.DEFAULT);
  const [verificationError, setVerificationError] = useState<string>('');
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const textLinkLabel = !showLoadingIcon ? t('profile.RegistrationSponsor.verify') : '';
  const isMultipleBusiness = internationalSponsorType === InternationalSponsorTypeEnum.MULTIPLE_BUSINESS;
  const isInternationalSponsor = internationalSponsorType === InternationalSponsorTypeEnum.INTERNATIONAL;
  const multipleBusinessTheme = isMultipleBusiness ? Theme.PURPLE : Theme.BLACK;
  const internationalSponsorTheme = isInternationalSponsor ? Theme.PURPLE : Theme.BLACK;
  const disableTextLink = sponsorNbr ? false : true;
  const dispatchHandler = (field: string, value: DynamicObjectInterface) => {
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          [field]: value,
        },
      }),
    );
  };

  useEffect(() => {
    // set default for international
    updateSponsorType(InternationalSponsorTypeEnum.INTERNATIONAL);
  }, []);

  useEffect(() => {
    if (!isInternational) {
      // reset values when set to No
      const payload = {
        internationalSponsorType: InternationalSponsorTypeEnum.INTERNATIONAL,
        internationalSponsor: null,
        internationalSponsorCountryCode: '',
        internationalSponsorTncCheckbox: false,
        internationalSposnorVerified: false,
      };
      updateState && updateState(payload);
    }
  }, [isInternational]);

  useEffect(() => {
    // Restore country from state
    if (internationalSponsorCountryCode) {
      const searchCountry = country?.find((c: CountryInterface) => c.pk === internationalSponsorCountryCode);
      if (searchCountry) {
        setCountrySearchTxt(searchCountry.countryName);
      }
    }
  }, [internationalSponsorCountryCode]);

  useEffect(() => {
    // Restore sponsor number from state
    if (internationalSponsor) {
      setSponsorNbr(internationalSponsor?.accountNumber?.toString());
    }
  }, [internationalSponsor]);

  const resetAgreementStatus = (additionalPayload?: DynamicObjectInterface) => {
    // Reset verified status and tnc if country, abo changed
    updateState &&
      updateState({
        internationalSposnorVerified: false,
        internationalSponsorTncCheckbox: false,
        ...additionalPayload,
      });
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          internationalSposnorVerified: false,
          internationalSponsorTncCheckbox: false,
          ...additionalPayload,
        },
      }),
    );
    setVerificationStatus(InputValidations.DEFAULT);
  };

  const countryName = currentCountry?.countryName ?? '';
  const sposorErrorMessage = verificationStatus === InputValidations.INVALID ? t(verificationError) : '';
  const sposorTheme = verificationStatus === InputValidations.INVALID ? Theme.ERROR : Theme.BLACK;

  const updateSponsorType = (sponsorType: InternationalSponsorTypeEnum) => {
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          isInternational: true,
          internationalSponsorType: sponsorType,
        },
      }),
    );
    updateState?.('internationalSponsorType', sponsorType);
  };
  const handleSelectSponsorTypeRadio = (e: React.MouseEvent<HTMLInputElement>) => {
    if (
      (isInternationalSponsor && e.currentTarget.id !== 'input-sponsor-type-international') ||
      (isMultipleBusiness && e.currentTarget.id === 'input-sponsor-type-international')
    ) {
      setCountrySearchTxt('');
    }
    updateSponsorType(
      e.currentTarget.id === 'input-sponsor-type-international'
        ? InternationalSponsorTypeEnum.INTERNATIONAL
        : InternationalSponsorTypeEnum.MULTIPLE_BUSINESS,
    );
  };

  const handleCountryChanged = (e: CountryInterface | DynamicObjectInterface) => {
    setCountrySearchTxt(e.countryName);
    resetAgreementStatus({
      internationalSponsorCountryCode: e.pk,
    });
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCountrySearchTxt(e.target.value);
    resetAgreementStatus();
  };

  const handleSponsorChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!showLoadingIcon) {
      setSponsorNbr(e.target.value);
      resetAgreementStatus();
    }
  };

  const onEnterVerify = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleVerifyABO();
    }
  };
  const ifInternationalSponsor = internationalSponsorCountryCode && sponsorNbr;

  const handleVerifyABO = () => {
    setShowLoadingIcon(true);
    if (ifInternationalSponsor) {
      const payload: SponsorSearchProps = {
        sponsorInputValue: sponsor.accountNumber,
        internationalSponsorAccountNumber: sponsorNbr,
        internationalSponsorCountryCode: internationalSponsorCountryCode,
        accountType: selectedAccountType,
      };
      profileApi
        .getInternationalSponsor({
          ...payload,
        })
        .then((res: SponsorSearchResponseInner[]) => {
          setShowLoadingIcon(false);
          const updateStateProp =
            res.length === 1
              ? {
                  internationalSponsor: {
                    accountNumber: res[0].internationalSponsor?.accountNumber,
                    accountName: res[0].internationalSponsor?.sponsorName,
                    location: res[0].internationalSponsor?.sponsorLocation,
                  },
                  internationalSposnorVerified: true,
                }
              : {
                  internationalSponsor: null,
                  internationalSposnorVerified: false,
                };

          const getStatus = () => {
            res.length === 1
              ? setVerificationStatus(InputValidations.DEFAULT)
              : setVerificationStatus(InputValidations.INVALID);
          };

          updateState && updateState(updateStateProp);
          dispatch(
            updateRegistrationData({
              selectSponsor: {
                ...selectSponsor,
                ...updateStateProp,
              },
            }),
          );
          getStatus();
        })
        .catch((err) => {
          setShowLoadingIcon(false);
          setVerificationStatus(InputValidations.INVALID);
          const errData = err?.responseBody;
          const errorMessage = errData?.subDetails?.[0]?.code || errData?.code || 'defaultError';
          setVerificationError(`profile.SinglePageRegistrationStep.ABOinputError.${errorMessage}`);
        });
    }
  };

  const handleCheckboxChanged = () => {
    updateState && updateState('internationalSponsorTncCheckbox', !internationalSponsorTncCheckbox);
    dispatchHandler('internationalSponsorTncCheckbox', !internationalSponsorTncCheckbox);
  };
  const handlerInternationlSponsorCrossButton = () => {
    setCountrySearchTxt('');
    setSponsorNbr('');
    dispatch(
      updateRegistrationData({
        selectSponsor: {
          ...selectSponsor,
          internationalSponsor: {},
          internationalSposnorVerified: false,
        },
      }),
    );
  };

  return (
    <div className={`${styles['international-sponsor']}`} data-testid="test-international-sponsor">
      <div className={`${styles['infoText']}`}>
        {t('profile.RegistrationSponsor.internationalInfoText', { country: countryName })}
      </div>

      <div className={`${styles['line']}`}></div>
      <div className={styles['sponsor-selection']} data-testid="test-international-sponsor-selection">
        {!internationalSposnorVerified ? (
          <div className={styles['input-sponsor-type-radio-fields']}>
            <RadioButton
              id="input-sponsor-type-international"
              data-testid="test-radio-sponsor-type-international"
              className={styles['radio-button']}
              label={t('profile.RegistrationSponsor.internationalSponsor')}
              checked={isInternationalSponsor}
              theme={internationalSponsorTheme}
              onClick={handleSelectSponsorTypeRadio}
            />
            <RadioButton
              id="input-sponsor-type-multiple"
              data-testid="test-radio-sponsor-type-multiple"
              className={styles['radio-button']}
              label={t('profile.RegistrationSponsor.multipleBusiness')}
              checked={isMultipleBusiness}
              theme={multipleBusinessTheme}
              onClick={handleSelectSponsorTypeRadio}
            />

            <DropDown
              id="country"
              data-testid="test-country-dropDown"
              name="country"
              data={country as DynamicObjectInterface}
              configData={{
                label: 'countryName',
                onSelect: (e: DynamicObjectInterface) => handleCountryChanged(e),
              }}
              value={countrySearchTxt}
              onChange={handleCountrySearch}
              dropdownLabel={t('profile.RegistrationSponsor.country')}
              isWithinDrawer={true}
            />
            <Input
              id="sponsor_id"
              name="sponsor_id"
              data-testid="test-international-sponsor-id"
              label={t('profile.RegistrationSponsor.sponsorABONumber')}
              onChange={handleSponsorChanged}
              onKeyDown={onEnterVerify}
              value={sponsorNbr}
              className={sponsorNbr?.length > 0 ? styles['enable-button'] : ''}
              ctaTextLink={{
                text: textLinkLabel,
                disabled: disableTextLink,
                id: 'btn_verify',
                onClick: handleVerifyABO,
              }}
              iconClassName="fa-spin"
              inputIcon={showLoadingIcon ? { iconName: 'spinner-third', iconStyle: IconStyle.SOLID } : undefined}
              status={verificationStatus}
              message={sposorErrorMessage}
              theme={sposorTheme}
            />
          </div>
        ) : (
          <>
            <div className={styles['intertaional-sponsor-heading']}>
              {t('profile.RegistrationSponsor.internationalSponsor')}
            </div>
            <SponsorCard
              abo={internationalSponsor}
              addHandler={handlerInternationlSponsorCrossButton}
              isSelected
              isInternationalABO={false}
            />
          </>
        )}

        <div className={styles['sponsor-tnc']}>
          {sponsorCms && <div>{sponsorCms.internationalSponsorTnc1}</div>}
          {sponsorCms && <div>{sponsorCms.internationalSponsorTnc2}</div>}
          <div className={styles['tnc-checkbox']}>
            <Checkbox
              data-testid="test-tnc-checkbox-agree"
              text={t('profile.RegistrationSponsor.iAgree')}
              checked={internationalSponsorTncCheckbox}
              onClick={handleCheckboxChanged}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
